import { useI18n } from 'vue-i18n';
import { useSettings } from './useSettings';

type WeatherCondition = App.ApiResources.WeatherCondition;
type WeatherConditionGroup = App.ApiResources.WeatherConditionGroup;
type TemperatureUnit = 'celsius' | 'fahrenheit';
type WindSpeedUnit = 'mph' | 'kmh' | 'ms';

/**
 * Forecasts are fetched and stored in metric - Celsius and metres per second -
 * so display is purely a conversion here. Changing the units setting therefore
 * takes effect at once, with nothing to refetch.
 */
const WIND_SPEED_FACTORS: Record<WindSpeedUnit, number> = {
  mph: 2.23694,
  kmh: 3.6,
  ms: 1,
};

const WIND_SPEED_LABELS: Record<WindSpeedUnit, string> = {
  mph: 'mph',
  kmh: 'km/h',
  ms: 'm/s',
};

/**
 * Formatting shared by every place a forecast is shown - the shift details card
 * and the scheduler's day summary - so that a temperature or a wind speed never
 * reads differently depending on which page you are on, and so the units
 * settings are honoured in one place rather than at each call site.
 */
export function useWeather() {
  const { t } = useI18n();

  /**
   * Temperature and wind units are configured separately because the two do not
   * pair up: the United Kingdom uses Celsius with miles per hour, Canada Celsius
   * with kilometres per hour. The fallbacks match the settings' own defaults,
   * which are what the application displayed before they existed.
   *
   * Read on each call rather than once when the composable is created, because
   * `useSettings` snapshots `window.settings` at the moment it runs - a
   * long-lived `useWeather()` would otherwise format against whatever the
   * settings were when it was constructed.
   */
  function temperatureUnit(): TemperatureUnit {
    const { getSetting } = useSettings();
    return getSetting('weather_temperature_units') === 'fahrenheit' ? 'fahrenheit' : 'celsius';
  }

  function windSpeedUnit(): WindSpeedUnit {
    const { getSetting } = useSettings();
    const configured = getSetting('weather_wind_units');
    return configured != null && configured in WIND_SPEED_FACTORS ? configured as WindSpeedUnit : 'mph';
  }

  /** Stored Celsius to whichever unit is configured. */
  function toDisplayTemperature(celsius: number): number {
    return temperatureUnit() === 'fahrenheit' ? (celsius * 9) / 5 + 32 : celsius;
  }

  function temperatureSuffix(): string {
    return temperatureUnit() === 'fahrenheit'
      ? t('global.degrees_fahrenheit_short', { deg: '°' })
      : t('global.degrees_centigrade_short', { deg: '°' });
  }

  /**
   * The weather-icons class for a condition. The font ships an icon per
   * OpenWeatherMap condition id, in a day and a night variant.
   */
  function weatherIconClass(condition: WeatherCondition | null | undefined): string {
    if (!condition) {
      return 'wi wi-na';
    }

    return `wi wi-owm-${condition.is_night ? 'night' : 'day'}-${condition.id}`;
  }

  function formatTemperature(temperature: number | string | null | undefined): string {
    if (temperature === null || temperature === undefined) {
      return '';
    }

    const value = toDisplayTemperature(Number.parseFloat(temperature.toString())).toFixed(1);
    return `${value} ${temperatureSuffix()}`;
  }

  /**
   * A day's spread. Collapses to a single figure when the two ends round to the
   * same value, so a flat day does not read as "12.0 - 12.0".
   */
  function formatTemperatureRange(
    minimum: number | string | null | undefined,
    maximum: number | string | null | undefined,
  ): string {
    if (minimum === null || minimum === undefined || maximum === null || maximum === undefined) {
      return '';
    }

    const low = toDisplayTemperature(Number.parseFloat(minimum.toString())).toFixed(1);
    const high = toDisplayTemperature(Number.parseFloat(maximum.toString())).toFixed(1);
    if (low === high) {
      return formatTemperature(minimum);
    }

    return `${low} - ${high} ${temperatureSuffix()}`;
  }

  function formatWindSpeed(metresPerSecond: number | string | null | undefined): string {
    if (metresPerSecond === null || metresPerSecond === undefined) {
      return '';
    }

    const unit = windSpeedUnit();
    const speed = (Number.parseFloat(metresPerSecond.toString()) * WIND_SPEED_FACTORS[unit]).toFixed(1);
    return `${speed} ${WIND_SPEED_LABELS[unit]}`;
  }

  /**
   * Icon for an apparent temperature, reflecting which way it pulls away from
   * the thermometer - wind chill down, humidex up - so the direction reads
   * before the number does.
   */
  function feelsLikeIcon(
    feelsLike: number | null | undefined,
    actual: number | null | undefined,
  ): string {
    if (feelsLike === null || feelsLike === undefined || actual === null || actual === undefined) {
      return 'fas fa-thermometer-half';
    }

    const difference = feelsLike - actual;
    if (difference <= -1) {
      return 'fas fa-temperature-low';
    }
    if (difference >= 1) {
      return 'fas fa-temperature-high';
    }

    return 'fas fa-thermometer-half';
  }

  /**
   * Colour for a condition, so that the conditions worth planning around stand
   * out from the ones that are simply weather.
   */
  function conditionColour(condition: WeatherCondition | null | undefined): string {
    const colours: Record<WeatherConditionGroup, string> = {
      thunderstorm: 'error',
      snow: 'error',
      rain: 'warning',
      drizzle: 'warning',
      atmosphere: 'dark-grey',
      clouds: 'dark-grey',
      clear: 'primary',
    };

    if (!condition) {
      return 'dark-grey';
    }

    return colours[condition.group] ?? 'dark-grey';
  }

  return {
    weatherIconClass,
    formatTemperature,
    formatTemperatureRange,
    formatWindSpeed,
    feelsLikeIcon,
    conditionColour,
  };
}
